import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

type SetEncryptionKeyDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (key: string) => void;
	pending?: boolean;
	count?: number;
};

export function SetEncryptionKeyDialog({
	open,
	onOpenChange,
	onSubmit,
	pending,
	count,
}: SetEncryptionKeyDialogProps) {
	const { t } = useTranslation();
	const [value, setValue] = useState("");

	useEffect(() => {
		if (open) setValue("");
	}, [open]);

	const trimmed = value.trim();
	const canSubmit = trimmed.length > 0 && !pending;

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;
		onSubmit(trimmed);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<form onSubmit={handleSubmit}>
					<DialogHeader>
						<DialogTitle>{t("meters.setEncryptionKeyTitle")}</DialogTitle>
						<DialogDescription>
							{typeof count === "number"
								? t("meters.setEncryptionKeyDescriptionBulk", { count })
								: t("meters.setEncryptionKeyDescription")}
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						<Input
							autoFocus
							value={value}
							onChange={(e) => setValue(e.target.value)}
							placeholder={t("meters.encryptionKeyPlaceholder")}
							aria-label={t("meters.encryptionKeyPlaceholder")}
						/>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={pending}
						>
							{t("meters.cancel")}
						</Button>
						<Button type="submit" disabled={!canSubmit}>
							{t("meters.saveEncryptionKey")}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
